import {
  colorMarcadorAAlmacenamiento,
  colorMarcadorDesdeAlmacenamiento,
  PanelPersonal,
  origenDesdeAlmacenamiento,
  type Marcador,
  type TarjetaValor,
} from '../../domain/entities/PanelPersonal';
import { crearError, traducirErrorSupabase } from '../../domain/errores';
import { aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';

const TABLA = 'user_dashboards';
const BUCKET = 'dashboard-images';

/** Tamaño máximo admitido para el plano de la instalación. */
const MAXIMO_IMAGEN_BYTES = 5 * 1024 * 1024;

function aLista(valor: unknown): Record<string, unknown>[] {
  if (Array.isArray(valor)) return valor as Record<string, unknown>[];

  if (typeof valor === 'string') {
    try {
      const parseado: unknown = JSON.parse(valor);
      return Array.isArray(parseado) ? (parseado as Record<string, unknown>[]) : [];
    } catch {
      return [];
    }
  }

  return [];
}

/** Un panel de antes sólo guarda `sensor_id`; de ahí sale su referencia. */
function textoDe(id: number | null): string | null {
  return id === null ? null : String(id);
}

/** Mantiene la coordenada dentro del plano aunque venga con un valor extraño. */
function aPorcentaje(valor: unknown): number {
  const n = aNumero(valor) ?? 50;
  return Math.min(100, Math.max(0, n));
}

function aMarcador(fila: Record<string, unknown>, indice: number): Marcador {
  return {
    id: aTexto(fila.id) ?? `m_${indice}`,
    x: aPorcentaje(fila.x),
    y: aPorcentaje(fila.y),
    color: colorMarcadorDesdeAlmacenamiento(fila.color),
    origen: origenDesdeAlmacenamiento(fila.origen),
    referencia: aTexto(fila.referencia) ?? textoDe(aNumero(fila.sensor_id)),
    sensorId: aNumero(fila.sensor_id),
    sensorNombre: aTexto(fila.sensor_nombre) ?? 'Sin sensor',
    sensorTipo: aTexto(fila.sensor_tipo) ?? '',
    sensorArea: aTexto(fila.sensor_area) ?? '',
    sensorUnidad: aTexto(fila.sensor_unidad) ?? '',
  };
}

function aTarjeta(fila: Record<string, unknown>, indice: number): TarjetaValor {
  return {
    id: aTexto(fila.id) ?? `t_${indice}`,
    etiqueta: aTexto(fila.label) ?? 'Sin título',
    descripcion: aTexto(fila.sub) ?? '',
    origen: origenDesdeAlmacenamiento(fila.origen),
    referencia: aTexto(fila.referencia) ?? textoDe(aNumero(fila.sensor_id)),
    sensorId: aNumero(fila.sensor_id),
    unidad: aTexto(fila.unit) ?? '',
    tipo: aTexto(fila.tipo) ?? '',
    area: aTexto(fila.area) ?? '',
  };
}

function comoPin(m: Marcador): Record<string, unknown> {
  return {
    id: m.id,
    x: m.x,
    y: m.y,
    color: colorMarcadorAAlmacenamiento(m.color),
    origen: m.origen,
    referencia: m.referencia,
    sensor_id: m.sensorId,
    sensor_nombre: m.sensorNombre,
    sensor_tipo: m.sensorTipo,
    sensor_area: m.sensorArea,
    sensor_unidad: m.sensorUnidad,
  };
}

function comoStat(t: TarjetaValor): Record<string, unknown> {
  return {
    id: t.id,
    label: t.etiqueta,
    sub: t.descripcion,
    origen: t.origen,
    referencia: t.referencia,
    sensor_id: t.sensorId,
    unit: t.unidad,
    tipo: t.tipo,
    area: t.area,
  };
}

/**
 * Guarda y recupera los paneles personales de cada usuario.
 *
 * Las claves de los campos guardados se conservan tal como estaban (`label`,
 * `sub`, `unit`, colores en inglés) para que los paneles que los usuarios ya
 * habían configurado sigan abriéndose sin migración.
 */
export class RepositorioPanel {
  /**
   * Todos los paneles de una persona, y cuál abrir.
   *
   * La colección vive en la columna `paneles`. Las columnas de siempre
   * —`site_name`, `image_url`, `pins`, `stats`— siguen describiendo el panel
   * activo: así lo guardado antes de que existieran varios se lee como una
   * colección de uno, sin convertir nada, y cualquier cosa que siga mirando
   * esas columnas encuentra lo que espera.
   */
  async obtenerTodos(
    usuarioId: string,
  ): Promise<{ paneles: PanelPersonal[]; activoId: string | null }> {
    const { data, error } = await supabase
      .from(TABLA)
      .select('site_name, image_url, pins, stats, paneles, panel_activo')
      .eq('user_id', usuarioId)
      .maybeSingle();

    if (error) throw traducirErrorSupabase(error);

    if (!data) {
      const primero = PanelPersonal.vacio(usuarioId);
      return { paneles: [primero], activoId: primero.id };
    }

    const fila = data as Record<string, unknown>;
    const guardados = aLista(fila.paneles);

    if (guardados.length > 0) {
      const paneles = guardados.map(
        (p, indice) =>
          new PanelPersonal({
            id: aTexto(p.id) ?? `p_${indice}`,
            usuarioId,
            nombreInstalacion: aTexto(p.nombre) ?? `Panel ${indice + 1}`,
            imagenUrl: aTexto(p.image_url),
            marcadores: aLista(p.pins).map(aMarcador),
            tarjetas: aLista(p.stats).map(aTarjeta),
          }),
      );

      const activoId = aTexto(fila.panel_activo);
      const existe = paneles.some((p) => p.id === activoId);

      return { paneles, activoId: existe ? activoId : (paneles[0]?.id ?? null) };
    }

    // Sin colección: lo de siempre, leído como un único panel.
    const unico = new PanelPersonal({
      id: aTexto(fila.panel_activo) ?? 'p_inicial',
      usuarioId,
      nombreInstalacion: aTexto(fila.site_name) ?? 'Mi instalación',
      imagenUrl: aTexto(fila.image_url),
      marcadores: aLista(fila.pins).map(aMarcador),
      tarjetas: aLista(fila.stats).map(aTarjeta),
    });

    return { paneles: [unico], activoId: unico.id };
  }

  async guardarTodos(
    usuarioId: string,
    paneles: readonly PanelPersonal[],
    activoId: string,
  ): Promise<void> {
    const activo = paneles.find((p) => p.id === activoId) ?? paneles[0];
    if (!activo) return;

    const { error } = await supabase.from(TABLA).upsert(
      {
        user_id: usuarioId,

        // El panel activo, también en las columnas de siempre.
        site_name: activo.nombreInstalacion,
        image_url: activo.imagenUrl,
        pins: activo.marcadores.map(comoPin),
        stats: activo.tarjetas.map(comoStat),

        panel_activo: activo.id,
        paneles: paneles.map((p) => ({
          id: p.id,
          nombre: p.nombreInstalacion,
          image_url: p.imagenUrl,
          pins: p.marcadores.map(comoPin),
          stats: p.tarjetas.map(comoStat),
        })),
      },
      { onConflict: 'user_id' },
    );

    if (error) throw traducirErrorSupabase(error);
  }

  /**
   * Sube el plano de la instalación y devuelve su dirección pública.
   *
   * Cada imagen va a una ruta con el identificador del usuario por delante,
   * que es lo que permite que las políticas del almacenamiento impidan a nadie
   * escribir en la carpeta de otro.
   */
  async subirImagen(usuarioId: string, archivo: File): Promise<string> {
    if (!archivo.type.startsWith('image/')) {
      throw crearError('DATOS_INVALIDOS', null, 'El archivo debe ser una imagen.');
    }
    if (archivo.size > MAXIMO_IMAGEN_BYTES) {
      throw crearError('DATOS_INVALIDOS', null, 'La imagen no puede superar los 5 MB.');
    }

    const extension = (archivo.type.split('/')[1] ?? 'png').split('+')[0];
    const ruta = `${usuarioId}/plano-${Date.now()}.${extension}`;

    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(ruta, archivo, { contentType: archivo.type, upsert: false });

    if (error) throw traducirErrorSupabase(error);

    const { data } = supabase.storage.from(BUCKET).getPublicUrl(ruta);
    return data.publicUrl;
  }
}

export const repositorioPanel = new RepositorioPanel();
